import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BarChart2, Building, Database, MapPin, Users, UserCircle,
  Activity, ShieldCheck, KeyRound, FileText, ChevronDown, LogOut, UserCog, UserCheck
} from 'lucide-react';
import Wordmark from './Wordmark';
import { authStorage } from '../../utils/storage';

const NAV = [
  {
    section: 'Overview',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: BarChart2, path: '/metrics' },
    ],
  },
  {
    section: 'Organization',
    items: [
      { id: 'units',           label: 'Units',           icon: Building, path: '/units' },
      { id: 'unit-management', label: 'Unit Management', icon: Database,  path: '/unit-management', adminOnly: true },
      { id: 'towns',           label: 'Towns',           icon: MapPin,    path: '/towns' },
    ],
  },
  {
    section: 'People',
    items: [
      { id: 'staff',        label: 'Staff',            icon: Users,       path: '/staff' },
      { id: 'coordinators', label: 'Area Coordinators', icon: UserCircle, path: '/area-coordinators' },
    ],
  },
  {
    section: 'Compliance',
    items: [
      { id: 'activity', label: 'Activity Feed',     icon: Activity,    path: '/audit-log' },
      { id: 'audits',   label: 'Membership Audits', icon: ShieldCheck, path: '/membership-audits' },
      { id: 'reports',  label: 'Reports',          icon: FileText,    path: '/reports' },
      { id: 'officer-changes', label: 'Officer Changes', icon: UserCheck, path: '/officer-changes', adminOnly: true },
    ],
  },
  {
    section: 'Admin',
    items: [
      { id: 'admin-users',       label: 'Users',           icon: KeyRound, path: '/admin/users',       adminOnly: true },
      { id: 'admin-reports',     label: 'Leadership & Activity', icon: FileText, path: '/admin/reports', adminOnly: true },
      { id: 'admin-assignments', label: 'Mass Assignment', icon: UserCog,  path: '/admin/assignments', adminOnly: true },
    ],
  },
];

function pathToId(pathname) {
  if (pathname === '/' || pathname === '/metrics') return 'dashboard';
  if (pathname.startsWith('/units/') || pathname.startsWith('/unit-info/')) return 'units';
  if (pathname === '/units') return 'units';
  if (pathname === '/unit-management') return 'unit-management';
  if (pathname === '/towns' || pathname.startsWith('/town/')) return 'towns';
  if (pathname === '/staff') return 'staff';
  if (pathname === '/area-coordinators') return 'coordinators';
  if (pathname === '/audit-log') return 'activity';
  if (pathname === '/membership-audits') return 'audits';
  if (pathname === '/officer-changes') return 'officer-changes';
  if (pathname === '/admin/users') return 'admin-users';
  if (pathname === '/admin/reports') return 'admin-reports';
  if (pathname === '/admin/assignments') return 'admin-assignments';
  return 'dashboard';
}

function initials(name) {
  if (!name) return 'DU';
  return name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();
}

function Sidebar({ onLogout, open }) {
  const navigate = useNavigate();
  const location = useLocation();
  const current = pathToId(location.pathname);

  const [collapsed, setCollapsed] = useState({ Admin: false });
  const [fullName, setFullName] = useState('');
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    setFullName(authStorage.getFullName() || authStorage.getUsername() || 'User');
    setUserRole(authStorage.getRole() || 'User');
  }, []);

  const isAdmin = ['Admin', 'Manager', 'admin', 'manager'].includes(userRole);

  return (
    <aside className={`sidebar${open ? ' open' : ''}`}>
      <div className="sidebar-brand">
        <Wordmark size={32} />
        <div className="sidebar-brand-text">DATAHOUSE</div>
      </div>

      {NAV.map(grp => {
        const visibleItems = grp.items.filter(it => !it.adminOnly || isAdmin);
        if (visibleItems.length === 0) return null;
        const isCollapsed = !!collapsed[grp.section];
        return (
          <div key={grp.section} className={`sidebar-section${isCollapsed ? ' collapsed' : ''}`}>
            <div
              className="sidebar-section-head"
              onClick={() => setCollapsed(c => ({ ...c, [grp.section]: !c[grp.section] }))}
            >
              <span>{grp.section}</span>
              <ChevronDown size={12} className="chev" />
            </div>
            <div className="sidebar-nav-list">
              {visibleItems.map(it => (
                <button
                  key={it.id}
                  className={`sidebar-item${current === it.id ? ' active' : ''}`}
                  onClick={() => navigate(it.path)}
                >
                  <it.icon size={18} />
                  <span>{it.label}</span>
                </button>
              ))}
            </div>
          </div>
        );
      })}

      <div className="sidebar-footer">
        <div className="avatar">{initials(fullName)}</div>
        <div className="who">
          <div className="n">{fullName}</div>
          <div className="r">{userRole}</div>
        </div>
        <button className="btn-icon" title="Sign out" onClick={onLogout}>
          <LogOut size={18} />
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;
